import { Grid } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import DataTable from './DataTable'
import { ReactElement, useCallback, useMemo } from 'react'
import { useDataTable } from './data-table.hook'
import { useConfirmDialog } from 'store/actions/ui.action'
import { keyBy } from 'lodash-es'
import { generatePath, useHistory } from 'react-router'
import { RowDefinition } from './Rows/RowDefinition'
import DataTableSearchField from 'components/SearchField/DataTableSearchField'
import { Show } from 'permission/Show'
import usePermission from 'hooks/usePermission/usePermission'
import { useTranslation } from 'react-i18next'
import { IButton } from './IButton'
import MobileIconButton from 'components/MobileIconButton/MobileIconButton'
import { ITableProps } from './ITableProps'
import DeleteSelectedButton from 'components/DeleteSelectedButton/DeleteSelectedButton'

const BUTTON_DELETE_TEXT = 'button.delete'
const BUTTON_NEW_TEXT = 'button.new'

interface CrudButtonProps {
    color?:
        | 'inherit'
        | 'primary'
        | 'secondary'
        | 'success'
        | 'error'
        | 'info'
        | 'warning'
        | undefined
    icon?: string | ReactElement
}

const NewButtonProps: CrudButtonProps = {
    color: 'info',
    icon: <AddIcon />,
}

export const DeleteButtonProps: CrudButtonProps = {
    color: 'secondary',
    icon: <DeleteIcon />,
}
export interface ITableOrder {
    /**
     * label of column
     */
    column: string
    /**
     * sort direction
     */
    direction: 'asc' | 'desc' | undefined
    /**
     * Unable to sort data by Unique col with Unique col
     */
    isUniqueColumn: boolean
}
export interface CrudDataTableProps<T>
    extends Omit<ITableProps<T>, 'selected' | 'onSelectAll' | 'onSelect'> {
    /**
     * text of New Button
     */
    newButtonText?: string

    /**
     * text of Delete Button
     */
    deleteButtonText?: string

    /**
     * set false to hide new button
     */
    useNewButton?: boolean

    /**
     * new route
     */
    newRoute?: string

    /**
     * edit route
     */
    editRoute?: string

    /**
     * Event handler called when user clicked on table row
     * if not defined, will navigate to `{editRoute}/?id={item[keyProp]}`
     */
    onRowClick?: (item: T) => void

    /**
     * Event handler called when user double clicked on table row
     */
    onRowDblClick?: (item: T) => void

    /**
     * Event handler called when user clicked on New Button
     * if not defined, will navigate to {newRoute}
     */
    onNew?: () => void

    /**
     * Event handler called when user toggle switch in data table
     * {key} is field named defined in IHeader.value (work only if IHeader.value is string type)
     * {toggledItem} row item where switch is under
     */
    onToggleSwitch?: (key: string, toggledItem: T, isForce?: boolean) => void

    /**
     * onDelete Event Handler
     */
    onDelete?: (selected: Array<T>) => void

    /**
     * Confirm onDelete
     */
    isConfirmBeforeDelete?: boolean

    /**
     * used when if filterRule is not defined, define list of columns to be filtered
     */
    filterColumns?: string[]

    /**
     * filter predicate function that called on every rows, with query string from SearchField
     */
    filterRule?: (item: T, query: string) => boolean

    /**
     * custom predicate function called after filterRule
     */
    customFilter?: (item: T) => boolean

    /**
     * if TRUE show loading indicator on datatable
     */
    loading?: boolean

    /**
     * if TRUE disabled control on data table
     */
    disabled?: boolean

    /**
     * hide search field (default: false)
     */
    hideSearchField?: boolean

    /**
     * Row Definition Object
     */
    rowOption?: RowDefinition<T>

    /**
     * disable new button
     */
    disabledNewBtn?: boolean
    addPermission?: number[]
    deletePermission?: number[]
    editPermission?: number[]

    /**
     * disable delete feature from data table
     */
    disabledDelete?: boolean

    /**
     * custom New Button
     */
    newButtonProps?: CrudButtonProps

    /**
     * custom Delete Button
     */
    deleteButtonProps?: CrudButtonProps
    paramName?: string
    /**
     * To provide HTML elements under search box. Ideal for placing additional filter components.
     */
    additionalFilterPanel?: React.ReactElement
    /** Additional button in right side*/
    rightButtonSection?: React.ReactElement

    /** To provide button before new button */
    prependActionButtons?: IButton[]

    /** To provide button after new button */
    appendActionButtons?: IButton[]

    /**
     * delay in milliseconds after no incoming input before trigger filter function
     */
    searchInputDelay?: number
    /**
     * disable pagination
     */
    noPagination?: boolean

    deleteDialogTitle?: string
    deleteDialogMessage?: string
    /**
     * tigger when selected some data
     */
    onSelected?: (dataIds: string[]) => void
    /**
     * set can click on row. set to true
     */
    editOnClick?: boolean

    onSyncSortedData?: (data: T[]) => void

    noExpandableData?: boolean

    /**
     * Show or hide the phone number lookup dialpad button.
     */
    useNumpad?: boolean

    /**
     * Show or hide the Caller ID lookup button.
     */
    useCallerId?: boolean
}

export default function CrudDataTable<T>({
    data,
    headers,
    itemKey,

    editRoute,
    paramName = 'id',
    onRowClick,
    onRowDblClick,
    onToggleSwitch,

    hideSearchField = false,
    searchInputDelay,
    filterColumns,
    filterRule,
    customFilter,

    onNew,
    newRoute,
    newButtonText = BUTTON_NEW_TEXT,
    useNewButton = true,
    disabledNewBtn = false,
    addPermission = [],
    newButtonProps = {},

    additionalFilterPanel = undefined,
    prependActionButtons,
    rightButtonSection,
    appendActionButtons,

    onDelete,
    isConfirmBeforeDelete = true,
    deletePermission = [],
    deleteButtonProps = {},
    deleteButtonText = BUTTON_DELETE_TEXT,
    deleteDialogTitle,
    deleteDialogMessage,
    disabledDelete = false,

    disabled = false,
    noPagination,
    rowOption,
    loading = false,
    editPermission = [],
    onSelected,
    editOnClick = true,
    onSyncSortedData = () => {},
    useNumpad = false,
    useCallerId = false,
    ...tableProps
}: CrudDataTableProps<T>) {
    const history = useHistory()
    const { t } = useTranslation()

    const _newButtonProps = {
        ...NewButtonProps,
        ...newButtonProps,
    }

    const _deleteButtonProps = {
        ...DeleteButtonProps,
        ...deleteButtonProps,
    }
    const defaultFilter = useCallback(
        (item: any, query: string) => {
            if (filterColumns && filterColumns.length > 0) {
                return filterColumns.some((c) =>
                    String(item[c]).toLowerCase().includes(query.toLowerCase()),
                )
            } else return true
        },
        [filterColumns],
    )

    const {
        selected,
        filteredData,
        queryText,
        onSelect,
        onSelectAll,
        setQueryText,
        setSelected,
    } = useDataTable({
        items: data,
        keyProp: itemKey,
        rowOption,
        filterRule: filterRule || defaultFilter,
        filterFunction: customFilter,
        onSelected,
    })
    const { deleteConfirmDialog } = useConfirmDialog()
    const itemMap = useMemo(() => keyBy(data, itemKey), [data, itemKey])

    const handleDelete = useCallback(() => {
        const selectedItems = selected.map((key) => itemMap[key])
        if (isConfirmBeforeDelete) {
            const deleteCallback = (result: boolean) => {
                if (result) {
                    onDelete?.(selectedItems)
                    setSelected([])
                }
            }
            deleteConfirmDialog(
                deleteCallback,
                deleteDialogTitle,
                deleteDialogMessage,
            )
        } else {
            onDelete?.(selectedItems)
            setSelected([])
        }
    }, [
        selected,
        isConfirmBeforeDelete,
        itemMap,
        deleteConfirmDialog,
        deleteDialogTitle,
        deleteDialogMessage,
        onDelete,
        setSelected,
    ])
    const { hasPermission } = usePermission()

    const handleOnNew = useCallback(() => {
        if (onNew) onNew()
        else if (newRoute) history.push(newRoute)
    }, [history, newRoute, onNew])

    const handleOnRowClick = useCallback(
        (item: any) => {
            if (onRowClick) onRowClick(item)
            else if (editRoute && hasPermission(editPermission)) {
                const route = generatePath(editRoute, {
                    [paramName]: item[itemKey],
                })
                history.push(route)
            } else {
                if (!tableProps.selectMode) return
                onSelect(item[itemKey])
            }
        },
        [
            onRowClick,
            editRoute,
            hasPermission,
            editPermission,
            paramName,
            itemKey,
            history,
            tableProps.selectMode,
            onSelect,
        ],
    )

    // When the CrudDataTable is disabled:
    // - Override rowOption with disabled rows, clicking, and selecting.
    // - Remove drag-drop functionality.
    if (disabled) {
        const newRowOption: RowDefinition<T> = {
            ...rowOption,
            disabledRow: () => {
                return disabled
            },
            disabledClick: () => {
                return disabled
            },
            disabledSelect: () => {
                return disabled
            },
            disabledHint: '',
        }
        rowOption = newRowOption

        tableProps.onDragDrop = undefined
    }

    return (
        <Grid container spacing={2}>
            {!hideSearchField && (
                <Grid container item xs={12} sm={6}>
                    <DataTableSearchField
                        onChange={(newValue) => setQueryText(newValue)}
                        value={queryText || ''}
                        delay={searchInputDelay}
                        useNumpad={useNumpad}
                        useCallerId={useCallerId}
                    />
                </Grid>
            )}
            {additionalFilterPanel && (
                <Grid item container>
                    {additionalFilterPanel}
                </Grid>
            )}
            <Grid
                container
                item
                justifyContent="space-between"
                alignItems="center"
                display={
                    disabledDelete &&
                    !prependActionButtons &&
                    !useNewButton &&
                    !appendActionButtons &&
                    !rightButtonSection
                        ? 'none'
                        : undefined
                }
                spacing={1}
            >
                <Grid item container xs="auto" alignItems="center">
                    {!disabledDelete && (
                        <Show when={deletePermission}>
                            <DeleteSelectedButton
                                handleOnDelete={handleDelete}
                                selectedCount={selected.length}
                                deleteButtonText={t(deleteButtonText)}
                                disabled={disabled || loading}
                                color={_deleteButtonProps.color}
                                startIcon={_deleteButtonProps.icon}
                            />
                        </Show>
                    )}
                </Grid>
                {rightButtonSection && (
                    <Grid item xs container justifyContent={'flex-end'}>
                        {rightButtonSection}
                    </Grid>
                )}
                <Grid
                    item
                    xs="auto"
                    container
                    justifyContent={'flex-end'}
                    spacing={1}
                >
                    {prependActionButtons &&
                        prependActionButtons.map((b) => (
                            <Grid item key={'prepend_btn_' + b.title}>
                                <MobileIconButton
                                    className="textUpper"
                                    startIcon={b.startIcon}
                                    disableElevation
                                    color={b.color ?? 'info'}
                                    onClick={() => b.onClick(selected)}
                                    disabled={b.disabled ?? false}
                                >
                                    {b.title}
                                </MobileIconButton>
                            </Grid>
                        ))}
                    {useNewButton && (
                        <Grid item>
                            <Show when={addPermission}>
                                <MobileIconButton
                                    className="textUpper"
                                    startIcon={_newButtonProps.icon}
                                    disableElevation
                                    color={_newButtonProps.color}
                                    onClick={handleOnNew}
                                    disabled={disabledNewBtn || disabled}
                                >
                                    {t(newButtonText)}
                                </MobileIconButton>
                            </Show>
                        </Grid>
                    )}
                    {appendActionButtons &&
                        appendActionButtons.map((b) => (
                            <Grid item key={'append_btn_' + b.title}>
                                <MobileIconButton
                                    className="textUpper"
                                    startIcon={b.startIcon}
                                    disableElevation
                                    color={b.color ?? 'info'}
                                    onClick={() => b.onClick(selected)}
                                    disabled={b.disabled ?? false}
                                >
                                    {b.title}
                                </MobileIconButton>
                            </Grid>
                        ))}
                </Grid>
            </Grid>
            <Grid container item>
                <DataTable
                    {...tableProps}
                    selectMode={
                        hasPermission(deletePermission) && !disabledDelete
                    }
                    itemKey={itemKey}
                    headers={headers}
                    loading={loading}
                    data={filteredData}
                    onSelect={onSelect}
                    selected={selected}
                    onSelectAll={onSelectAll}
                    onRowClick={handleOnRowClick}
                    onRowDblClick={onRowDblClick}
                    onToggleSwitch={onToggleSwitch}
                    rowOption={rowOption}
                    editPermission={editPermission}
                    usePagination={!noPagination}
                    disabled={disabled}
                    editOnClick={editOnClick}
                    onSyncSortedData={onSyncSortedData}
                />
            </Grid>
        </Grid>
    )
}
